'use client';

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CollegeCutoff } from '@/lib/services/examService';

interface ExamCutoffChartProps {
  cutoffs: CollegeCutoff[];
  unit: string;
}

const CATEGORY_COLORS = {
  General: 'var(--secondary)',
  OBC: 'var(--primary)',
  SC: '#16A34A',
  ST: '#7C3AED',
  EWS: '#D97706',
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card-base p-3 shadow-modal min-w-[160px]">
      <p className="font-bold text-foreground text-sm mb-2 border-b border-border pb-1">{label}</p>
      {payload.map((entry) => (
        <div key={`tooltip-${entry.name}`} className="flex items-center justify-between gap-4 text-xs py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">{entry.name}</span>
          </div>
          <span className="font-bold font-tabular text-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function ExamCutoffChart({ cutoffs, unit }: ExamCutoffChartProps) {
  const chartData = cutoffs.map((c) => ({
    name: c.collegeName.replace('Indian Institute of ', 'II').replace(' University', ' Univ.'),
    General: c.general,
    OBC: c.obc,
    SC: c.sc,
    ST: c.st,
    EWS: c.ews,
  }));

  return (
    <div className="bg-muted rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-foreground">Cutoff Comparison by Category (2025)</h4>
        <span className="text-xs text-muted-foreground">Unit: {unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.5 }} />
          <Legend
            iconType="square"
            iconSize={10}
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
          />
          {Object.entries(CATEGORY_COLORS).map(([category, color]) => (
            <Bar key={`bar-${category}`} dataKey={category} fill={color} radius={[3, 3, 0, 0]} maxBarSize={24} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}